import { Button, Card, CardContent, Grid, IconButton, Tooltip } from "@mui/material";

import { usersApiKeyStore } from "../../../../api/users-api-key-store";
import { FormInput } from "../../../../components/hook-form/form-input.component";
import { skillSetApiKeyStore } from "../../../../api/masters/skill-set-api-key-store";
import { equipmentApiKeyStore } from "../../../../api/masters/equipment-api-key-store";
import { departmentsApiKeyStore } from "../../../../api/masters/department-api-key-store";
import { marketAreaApiKeyStore } from "../../../../api/masters/market-area-api-key-store";
import { FormAutoCompleteSelectDynamic } from "../../../../components/hook-form/form-autocomplete-select.dynamic.component";
import { FormAutoCompleteMultiSelectDynamic } from "../../../../components/hook-form/form-autocomplete-multi-select.dynamic.component";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Icons } from "../../../../assets/icons";
import { FormSelectDynamic } from "../../../../components/hook-form/form-select.dynamic.component";
import { useLoadData } from "../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../models/api-response";
import { useEffect, useState } from "react";
import { useSnackbar } from "../../../../hooks/use-snackbar.hook";
import { LoadingIndicator } from "../../../../components/loading-indicator.component";

const styling = {
    paperStyle: { padding: '20px 0px 0' },
    inlineStyle: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: 10,
        width: '100%'
    }
};

export const EmployeeDetails = () => {

    const { showSnackbar } = useSnackbar();
    const [disableManager, setDisableManager] = useState<boolean>(false);
    const { setValue, getValues, control } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: `userSkillsets`,
        keyName: 'usID',
    });
    const [departmentID, setDepartmanetID] = useState<number>(0);

    const { isLoading, isFetching, data } = useLoadData<ApiResponse<any>>(departmentsApiKeyStore.getDepartmentById(departmentID), !!departmentID);

    useEffect(() => {
        if (data) {
            const manager = { managerID: data?.data?.managerID ?? 0, managerName: data?.data?.managerName ?? '' }
            if (manager?.managerID > 0)
                setValue('userManagers', [{ userID: manager?.managerID, fullName: manager?.managerName }])
            else {
                setValue('userManagers', [])
                showSnackbar('No managers are mapped to the department.', 'warning');
            }
        }
    }, [data])

    useEffect(() => {
        if (getValues('userDepartments')) {
            setDisableManager(getValues('userDepartments')?.departmentID > 0 ? true : false)
        }
    }, [getValues('userDepartments')])

    const handleAddSkillSet = () => {
        append({
            skillsetID: 0,
            licenseNumber: ""
        })
    }

    const handleDepartmentChange = (value: any) => {
        setDepartmanetID(value?.departmentID ?? 0)
        setDisableManager(true);
        if (value == null) {
            setValue('userManagers', [])
            setDisableManager(false);
        }

    }

    return (
        <Card elevation={0} style={styling.paperStyle}>
            <CardContent className="formContainer">
                <Grid container spacing={2}>
                    <Grid item lg={6} sm={6} xl={6} xs={6}>
                        <FormAutoCompleteMultiSelectDynamic
                            name="userEquipments"
                            label="Equipment"
                            getQueryKeyConfig={() => equipmentApiKeyStore.getEquipments()}
                            optionsLabelKey="equipmentName"
                            optionsValueKey="equipmentID"
                        />
                    </Grid>
                    <Grid item lg={6} sm={6} xl={6} xs={6}>
                        <FormAutoCompleteMultiSelectDynamic
                            name="userManagers"
                            label="Reporting Manager"
                            disabled={disableManager}
                            getQueryKeyConfig={() => usersApiKeyStore.getUsers()}
                            optionsLabelKey="fullName"
                            optionsValueKey="userID"
                            isParentLoading={isLoading || isFetching}
                        />
                    </Grid>
                    <Grid item lg={6} sm={6} xl={6} xs={6}>
                        <FormAutoCompleteSelectDynamic
                            name="userDepartments"
                            label="Department"
                            onChange={handleDepartmentChange}
                            getQueryKeyConfig={() => departmentsApiKeyStore.getDepartments()}
                            optionsValueKey="departmentID"
                            optionsLabelKey="departmentName"
                        />
                    </Grid>
                    <Grid item lg={6} sm={6} xl={6} xs={6}>
                        <FormAutoCompleteMultiSelectDynamic
                            name="userMarketAreas"
                            label="Market Areas"
                            getQueryKeyConfig={() => marketAreaApiKeyStore.getMarketAreas()}
                            optionsLabelKey="marketAreaName"
                            optionsValueKey="marketAreaID"
                        />
                    </Grid>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="flex-start"
                        >
                            <Button
                                className="btn btn-primary"
                                variant="text"
                                onClick={handleAddSkillSet}
                            >
                                Add Skill
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        {fields.map((field, index) => (
                            <div key={field.usID} style={styling.inlineStyle}>
                                <Grid container spacing={2}>
                                    <Grid item lg={6} sm={6} xl={6} xs={6}>
                                        <FormSelectDynamic
                                            name={`userSkillsets.${index}.skillsetID`}
                                            label="Skills"
                                            required
                                            getQueryKeyConfig={() => skillSetApiKeyStore.getSkillSets()}
                                            optionsValueKey="skillsetID"
                                            optionsLabelKeys={['skillsetName']}
                                        />
                                    </Grid>
                                    <Grid item lg={5.5} sm={5.5} xl={5.5} xs={5.5}>
                                        <FormInput label="License Number" name={`userSkillsets.${index}.licenseNumber`} />
                                    </Grid>
                                    <Grid item lg={0.5} sm={0.5} xl={0.5} xs={0.5}>
                                        <IconButton aria-label="delete" onClick={() => remove(index)} >
                                            <Tooltip title={'Delete Skill'}>
                                                <img src={Icons.Delete} alt="DELETE" />
                                            </Tooltip>
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </div>
                        ))}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}